import { Controller } from "@hotwired/stimulus"
import type { Dropdown } from "bootstrap"

/**
 * Controls the application theme mode (light/dark/system).
 *
 * Usage:
 *   <div data-controller="theme-mode">
 *     <a href="#" data-action="click->theme-mode#setLight">Light</a>
 *     <a href="#" data-action="click->theme-mode#setDark">Dark</a>
 *     <a href="#" data-action="click->theme-mode#setSystem">System</a>
 *   </div>
 */
export default class extends Controller {
  static targets = ["menu"]

  declare readonly menuTarget: HTMLElement
  declare readonly hasMenuTarget: boolean

  setLight(event: Event) {
    event.preventDefault()
    this.setTheme("light")
  }

  setDark(event: Event) {
    event.preventDefault()
    this.setTheme("dark")
  }

  setSystem(event: Event) {
    event.preventDefault()
    this.setTheme("system")
  }

  private mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null

  connect() {
    // Check if current mode is system and add listener
    try {
      const storedMode = localStorage.getItem("data-bs-theme")
      if (storedMode === "system") {
        this.addSystemListener()
      }
    } catch {
      // localStorage may be unavailable in private browsing
    }
  }

  disconnect() {
    this.removeSystemListener()
  }

  private setTheme(mode: "light" | "dark" | "system") {
    let resolvedTheme = mode

    if (mode === "system") {
      resolvedTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
    }

    // Disable transitions during theme switch to prevent flash
    document.documentElement.classList.add("theme-mode-switching")

    // Update the document theme
    document.documentElement.setAttribute("data-bs-theme", resolvedTheme)

    // Re-enable transitions after a short delay
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove("theme-mode-switching")
      })
    })

    // Store the user's preference
    try {
      localStorage.setItem("data-bs-theme", mode)
    } catch (e) {
      console.warn("Failed to store theme preference:", e)
    }

    // Manage system preference listener
    if (mode === "system") {
      this.addSystemListener()
    } else {
      this.removeSystemListener()
    }

    // Close all parent dropdowns after selection
    this.closeParentDropdowns()
  }

  private closeParentDropdowns() {
    // Walk up the DOM tree and close all dropdown menus containing this controller
    let current = this.element.closest(".dropdown-menu")
    while (current) {
      // Find the toggle that controls this menu (previous sibling with data-bs-toggle)
      const toggle = current.previousElementSibling?.matches('[data-bs-toggle="dropdown"]')
        ? current.previousElementSibling as HTMLElement
        : current.parentElement?.querySelector<HTMLElement>(':scope > [data-bs-toggle="dropdown"]')

      if (toggle) {
        const dropdown = globalThis.bootstrap.Dropdown.getInstance(toggle) as Dropdown | null
        dropdown?.hide()
      }

      // Move up to the next parent dropdown menu
      current = current.parentElement?.closest(".dropdown-menu") ?? null
    }
  }

  private addSystemListener() {
    this.removeSystemListener()
    this.mediaQueryListener = (e: MediaQueryListEvent) => {
      document.documentElement.setAttribute("data-bs-theme", e.matches ? "dark" : "light")
    }
    window.matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", this.mediaQueryListener)
  }

  private removeSystemListener() {
    if (this.mediaQueryListener) {
      window.matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", this.mediaQueryListener)
      this.mediaQueryListener = null
    }
  }
}
